import { messageAPI } from '@/services-ts/axios'
import type {
  IGetAnnouncement,
  IGetAnnouncementUserDisplayList,
  IPayloadAnnouncement,
  IPayloadAnnouncementCategory,
} from '@/types/Request'
import type { IAnnouncementStore } from '@/types/Store/State'
import { Alert, Format } from '@/utils-ts'
import { defineStore } from 'pinia'
import { useFilterStore } from '../'

export const useAnnouncementStore = defineStore('announcement', {
  state: (): IAnnouncementStore => ({
    announcementList: {
      data: [],
      total: 0,
    },
    categories: [],
  }),

  actions: {
    async GetAnnouncementCategoryList() {
      const { status, data } = await messageAPI.AnnouncementServices.GetAnnouncementCategoryList()
      this.categories = status ? data : []
    },

    async CreateAnnouncementCategory(payload: IPayloadAnnouncementCategory) {
      const { status } = await messageAPI.AnnouncementServices.CreateAnnouncementCategory(payload)

      if (status) {
        await this.GetAnnouncementCategoryList()
        this.ALERT(Format('admin_global_success'), 'success')
      }
      return status
    },

    async UpdateAnnouncementCategory(payload: IPayloadAnnouncementCategory) {
      const { status } = await messageAPI.AnnouncementServices.UpdateAnnouncementCategory(payload)

      if (status) {
        await this.GetAnnouncementCategoryList()
        this.ALERT(Format('admin_global_updated'), 'success')
      }
      return status
    },

    async DeleteAnnouncementCategory(id: string) {
      const { status } = await messageAPI.AnnouncementServices.DeleteAnnouncementCategory(id)

      if (status) {
        await this.GetAnnouncementCategoryList()
        this.ALERT(Format('admin_global_deleted'), 'success')
      }
      return status
    },

    async GetAnnouncementList(payload: IGetAnnouncement) {
      const { status, data, total } =
        await messageAPI.AnnouncementServices.GetAnnouncementList(payload)
      this.announcementList = status ? { data, total: total || 0 } : { data: [], total: 0 }
    },

    async CreateAnnouncement(payload: IPayloadAnnouncement) {
      const { status, data } = await messageAPI.AnnouncementServices.CreateAnnouncement(payload)
      if (status) {
        const filter = payload.FilterId
          ? useFilterStore().GetFilterByIdSync(payload.FilterId)
          : null
        this.announcementList = {
          data: [{ ...data, filterName: filter?.name ?? null }, ...this.announcementList.data],
          total: this.announcementList.total + 1,
        }

        this.ALERT(Format('admin_global_success'), 'success')
      }
      return status
    },

    async UpdateAnnouncement(payload: IPayloadAnnouncement) {
      const { status, data } = await messageAPI.AnnouncementServices.UpdateAnnouncement(payload)

      if (status) {
        const index = this.announcementList.data.findIndex(x => x.id === payload.Id)
        const filter = payload.FilterId
          ? useFilterStore().GetFilterByIdSync(payload.FilterId)
          : null

        const currentAnnouncement = this.announcementList.data[index]
        if (currentAnnouncement) {
          this.announcementList.data[index] = {
            ...data,
            filterName: filter?.name ?? null,
            uniqueDisplayCount: currentAnnouncement.uniqueDisplayCount,
            reactionsCount: currentAnnouncement.reactionsCount,
            isReadAcceptReaction: payload.IsReadAcceptReaction,
          }
        }

        this.ALERT(Format('admin_global_updated'), 'success')
      }
      return status
    },

    async DeleteAnnouncement(id: string) {
      const { status } = await messageAPI.AnnouncementServices.DeleteAnnouncement(id)
      if (status) {
        this.announcementList.data = this.announcementList.data.filter(x => x.id !== id)
        this.announcementList.total -= 1

        this.ALERT(Format('admin_global_deleted'), 'success')
      }
      return status
    },

    async GetAnnouncementUserDisplayList(payload: IGetAnnouncementUserDisplayList) {
      const { data } = await messageAPI.AnnouncementServices.GetAnnouncementUserDisplayList(payload)
      return data
    },

    async GetAnnouncementReactions(announcementId: string) {
      const { data } =
        await messageAPI.AnnouncementServices.GetAnnouncementReactions(announcementId)
      return data
    },

    async GetAnnouncementExcel() {
      await messageAPI.AnnouncementServices.GetAnnouncementExcel()
    },

    ALERT(message: string, type: 'success' | 'danger') {
      void Alert({ message, type })
    },
  },
})
